import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowLeft, Calendar, Clock, User, Share2 } from 'lucide-react';

const BookkeepingBlogPost = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-[#1a1a1a]">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ 
              backgroundImage: 'url(https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&h=800&fit=crop)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/80 via-[#1a1a1a]/90 to-[#1a1a1a]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 pt-12">
          {/* Back Link */}
          <Link 
            to="/community" 
            className="inline-flex items-center text-white/60 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Community
          </Link>

          {/* Category */}
          <div className="mb-6">
            <span className="bg-[#8D6E63] text-white text-xs font-medium px-4 py-1.5 rounded-full">
              Book-keeping
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Why Your Bookkeeping is Dictating Your Growth
          </h1>

          {/* Subtitle */}
          <p className="text-xl text-white/70 mb-8 max-w-3xl">
            Book-keeping Best Practices for Growing SMBs
          </p>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-white/60 text-sm">
            <div className="flex items-center">
              <User className="w-4 h-4 mr-2" />
              <span>Trueledger Team</span>
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              <span>December 5, 2025</span>
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              <span>8 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Lead Paragraph */}
        <div className="mb-12">
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-light">
            Most business leaders view bookkeeping as a necessary evil—a tedious, back-office chore focused entirely on compliance and recording the past. But what if that mindset is exactly what's throttling your company's growth?
          </p>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 leading-relaxed mb-8">
            When you are scaling a business, basic bookkeeping isn't enough. To truly prepare for growth, you have to transform your financial processes from a historical ledger into a strategic compass. Here is how high-performing, growth-ready businesses leverage their books not just to survive, but to scale.
          </p>

          {/* Section 1 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">1</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                The Illusion of Strategy Without Clarity
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-4">
                The very first priority for any growing business is absolute financial clarity, and that begins with an impeccably clean foundation.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you are mixing personal and business finances, using inconsistent accounting policies, or miscategorizing transactions, your foundation is built on sand. When financial records are highly structured and completely reliable, leadership can actually trust the numbers guiding their key decisions.
              </p>
              <div className="bg-stone-50 border-l-4 border-[#3E2723] p-6 my-6">
                <p className="text-gray-800 font-medium italic">
                  "The hard truth: Without clean books, even the most brilliant business strategy is just an expensive guessing game."
                </p>
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">2</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                Stop Recording the Past and Illuminate the Present
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-4">
                Basic bookkeeping records what already happened. Strategic bookkeeping creates total visibility into exactly what is happening right now.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Modern accounting systems must provide real-time, actionable insight into your revenue, expenses, margins, and cash flow. As a business leader, you should be able to answer these questions instantly:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#8D6E63] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-700">Which specific products or services are generating the highest margins?</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#8D6E63] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-700">How are your operating expenses trending month over month?</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#8D6E63] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-700">Is your current growth actually profitable, or is it merely creating more workload?</span>
                </li>
              </ul>
              <p className="text-gray-600 leading-relaxed">
                When your financial data is accessible and timely, your decision-making transforms from hesitant and slow to confident and rapid.
              </p>
            </div>
          </div>

          {/* Section 3 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">3</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                The Profitability Trap: Cash Flow as a Strategic Metric
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <div className="bg-red-50 border border-red-100 p-6 mb-6 rounded-lg">
                <p className="text-red-800 font-medium">
                  It is a brutal reality of business: companies rarely die from a lack of profitability; they die from a lack of cash.
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Poor cash flow management is one of the most common reasons growing businesses collapse under their own weight.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Strategic bookkeeping doesn't just look at the bottom line; it obsessively tracks the movement of money through:
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-stone-50 p-4 rounded-lg">
                  <p className="text-gray-800 font-medium">Accounts receivable cycles</p>
                </div>
                <div className="bg-stone-50 p-4 rounded-lg">
                  <p className="text-gray-800 font-medium">Payables management</p>
                </div>
                <div className="bg-stone-50 p-4 rounded-lg">
                  <p className="text-gray-800 font-medium">Upcoming tax liabilities</p>
                </div>
                <div className="bg-stone-50 p-4 rounded-lg">
                  <p className="text-gray-800 font-medium">Recurring operational commitments</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                By maintaining a highly disciplined view of cash flow, leadership can anticipate future funding needs, aggressively negotiate better payment terms, and maintain rock-solid operational stability—even during periods of explosive growth.
              </p>
            </div>
          </div>

          {/* Section 4 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">4</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                The Ritual of Leadership: Monthly Financial Reviews
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-4">
                High-performing companies do not treat financial reviews as an annoyance from the accounting department; they treat them as a sacred leadership ritual.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                At an absolute minimum, management must sit down monthly to review the holy trinity of financial reporting:
              </p>
              <div className="bg-[#3E2723] text-white p-8 rounded-lg mb-6">
                <h4 className="text-lg font-semibold mb-4 text-[#8D6E63]">The Holy Trinity of Financial Reporting</h4>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-4 text-sm font-bold">1</span>
                    <span>The Profit and Loss Statement</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-4 text-sm font-bold">2</span>
                    <span>The Balance Sheet</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-4 text-sm font-bold">3</span>
                    <span>The Cash Flow Statement</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                This is not a simple accounting exercise. It is a critical diagnostic session used to identify hidden trends, detect operational inefficiencies, and catch the early warning signals of broader issues. When this review becomes a non-negotiable routine, a business officially graduates from reactive management to proactive control.
              </p>
            </div>
          </div>

          {/* Section 5 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">5</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                Operational Resilience Through Radical Preparedness
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-4">
                Strong documentation practices are not just for the IRS; they are the bedrock of operational resilience.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                By maintaining meticulously organized records of invoices, contracts, payroll reports, and expense documentation, your business is perpetually prepared for tax filings, sudden audits, and rigorous due diligence processes.
              </p>
              <div className="bg-stone-50 border-l-4 border-[#8D6E63] p-6 my-6">
                <p className="text-gray-800 font-medium">
                  This level of "audit-ready" preparedness becomes the difference between successfully closing a deal and watching it fall apart when you pursue external funding, strategic acquisitions, or major market expansions.
                </p>
              </div>
            </div>
          </div>

          {/* Section 6 */}
          <div className="mb-12">
            <div className="flex items-start mb-4">
              <span className="text-4xl font-bold text-[#3E2723] mr-4">6</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                The Ultimate Pivot: From Back-Office to Boardroom
              </h2>
            </div>
            <div className="pl-12 border-l-2 border-[#8D6E63]/30">
              <p className="text-gray-600 leading-relaxed mb-4">
                As your business scales, your bookkeeping cannot remain stagnant—it must forcefully evolve alongside you.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Your financial processes are no longer just about balancing the books; they must actively support your growth strategy, including:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#3E2723] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-700">Executing expansion into new markets</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#3E2723] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-700">Structuring hiring and compensation planning</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#3E2723] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-700">Conducting deep pricing and margin analysis</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#3E2723] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-700">Guiding major investment decisions</span>
                </li>
              </ul>
              <div className="bg-gradient-to-r from-[#3E2723] to-[#5D4037] text-white p-8 rounded-lg">
                <p className="text-lg font-medium leading-relaxed">
                  At this critical stage, bookkeeping completely sheds its identity as a back-office function and takes its rightful place as a primary, strategic input for executive leadership.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Share & CTA Section */}
        <div className="mt-16 pt-12 border-t border-stone-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Share this article:</span>
              <button className="p-2 bg-stone-100 rounded-full hover:bg-stone-200 transition-colors">
                <Share2 className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <a href="mailto:hello@trueledger.com?subject=Bookkeeping%20Services%20Inquiry&body=Hi%20Trueledger%20Team,%0A%0AI%20read%20your%20article%20on%20bookkeeping%20best%20practices%20and%20would%20like%20to%20learn%20more%20about%20your%20services.%0A%0AThank%20you.">
              <button className="bg-[#3E2723] text-white px-8 py-3 rounded-lg hover:bg-[#5D4037] transition-colors">
                Transform Your Bookkeeping Today
              </button>
            </a>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BookkeepingBlogPost;
